/**
 * `/instance/arranged-access`: every Arranged Access standing on this ELASO
 * Instance, in every Organisation, switched-off ones included, with End access
 * on each, a colleague's included
 * (docs/adr/32-the-root-organisation-and-arranged-access.md).
 *
 * The reason and the date come from each grant's origin, never from the
 * Administrative Audit Log, which nothing in the application reads
 * (docs/adr/35-the-administrative-audit-log.md). The guard, the socket claim
 * and the shell live on the layout route (`instance/route.tsx`).
 */
import type { InstanceArrangedAccessRow } from "@elaso/shared/types/organization";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { describePerson } from "@/components/admin/describe-person";
import { useInstanceAdmission } from "@/components/instance/instance-admission";
import {
	formatSince,
	SEARCH_FROM,
} from "@/components/organization/arranged-access";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { useKeepFocus } from "@/components/ui/use-keep-focus";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/instance/arranged-access")({
	component: InstanceArrangedAccessPage,
});

type Listing =
	| { status: "loading" }
	| { status: "failed" }
	| { status: "ready"; grants: InstanceArrangedAccessRow[] };

const errorMessage = (err: unknown) =>
	err instanceof Error ? err.message : String(err);

const grantKey = (grant: InstanceArrangedAccessRow) =>
	`${grant.workspaceId}:${grant.userId}`;

/** How many grants, not people: one person may hold several. */
const describeGrants = (count: number) =>
	count === 1 ? "1 grant" : `${count} grants`;

function InstanceArrangedAccessPage() {
	const { instanceManagerName } = useInstanceAdmission();
	const [listing, setListing] = useState<Listing>({ status: "loading" });
	const [ending, setEnding] = useState<InstanceArrangedAccessRow | null>(null);
	const keepFocus = useKeepFocus<HTMLDivElement>();
	// Only the latest read may become the list: the reload after an end can
	// overtake one still in flight.
	const latestRead = useRef(0);

	const load = useCallback(async () => {
		latestRead.current += 1;
		const read = latestRead.current;
		try {
			const { arranged } = await ws.sendWithPromise<{
				arranged: InstanceArrangedAccessRow[];
			}>({
				id: ulid(),
				procedure: "instanceAdmin.listArrangedAccess",
				data: {},
			});
			if (read === latestRead.current) {
				setListing({ status: "ready", grants: arranged });
			}
		} catch {
			// A failed read is shown as one, never as nobody holding any
			// (docs/adr/20-a-failed-permission-read-never-means-no-permissions.md).
			if (read === latestRead.current) setListing({ status: "failed" });
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const end = useCallback(
		async (grant: InstanceArrangedAccessRow) => {
			try {
				await ws.sendWithPromise({
					id: ulid(),
					procedure: "instanceAdmin.endArrangedAccess",
					data: {
						organizationId: grant.organizationId,
						workspaceId: grant.workspaceId,
						userId: grant.userId,
					},
				});
				toast.success(
					`Ended ${describePerson(grant).label}'s access to ${grant.workspaceName}`
				);
				await load();
			} catch (err) {
				toast.error(`Could not end access: ${errorMessage(err)}`);
			}
		},
		[load]
	);

	function renderListing() {
		if (listing.status === "failed") {
			return (
				<div className="flex items-center gap-3">
					<p className="text-muted-foreground text-sm">
						Could not load the Arranged Access.
					</p>
					<Button
						onClick={() => {
							setListing({ status: "loading" });
							load();
						}}
						size="sm"
						type="button"
						variant="outline"
					>
						Try again
					</Button>
				</div>
			);
		}
		if (listing.status === "loading") {
			return (
				<p className="text-muted-foreground text-sm">
					Loading Arranged Access…
				</p>
			);
		}
		if (listing.grants.length === 0) {
			return (
				<p className="text-muted-foreground text-sm">
					No Arranged Access is standing on this Instance.
				</p>
			);
		}
		return <GrantList grants={listing.grants} onEnd={setEnding} />;
	}

	return (
		<div className="flex flex-col items-center gap-4 p-4 md:p-8">
			<Card className="w-full max-w-4xl">
				<CardHeader>
					<CardTitle>
						<h2>Arranged Access</h2>
					</CardTitle>
					<CardDescription>
						Everyone from {instanceManagerName} who has reached into an
						Organisation's workspace, and why. You can end any of them, a
						colleague's included. Every grant and every removal is recorded.
					</CardDescription>
				</CardHeader>
				{/* Takes focus itself once the last grant has ended, so a screen reader reads that none is left. */}
				<CardContent ref={keepFocus} tabIndex={-1}>
					{renderListing()}
				</CardContent>
			</Card>
			<ConfirmDialog
				confirmLabel="End access"
				description="They will no longer be able to read anything in it. The removal is recorded with your name."
				onConfirm={() => {
					if (ending) end(ending);
				}}
				onOpenChange={(next) => {
					if (!next) setEnding(null);
				}}
				open={ending !== null}
				title={
					ending
						? `End ${describePerson(ending).label}'s access to ${ending.workspaceName}?`
						: ""
				}
			/>
		</div>
	);
}

/**
 * Each grant with where it reaches, why and since when. Past a handful it can
 * be searched by holder, address, Organisation, workspace or reason.
 */
function GrantList({
	grants,
	onEnd,
}: {
	grants: InstanceArrangedAccessRow[];
	onEnd: (grant: InstanceArrangedAccessRow) => void;
}) {
	const [search, setSearch] = useState("");
	const searchable = grants.length >= SEARCH_FROM;
	const needle = searchable ? search.trim().toLowerCase() : "";
	const shown = needle
		? grants.filter((grant) =>
				[
					grant.displayName,
					grant.email,
					grant.organizationName,
					grant.workspaceName,
					grant.reason ?? "",
				]
					.join(" ")
					.toLowerCase()
					.includes(needle)
			)
		: grants;

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-muted-foreground text-sm">
					{describeGrants(grants.length)}
				</p>
				{searchable ? (
					<Input
						aria-label="Search arranged access"
						className="h-8 w-48"
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search"
						value={search}
					/>
				) : null}
			</div>
			<ul className="divide-y rounded-md border px-3">
				{shown.map((grant) => {
					const holder = describePerson(grant);
					return (
						<li
							className="flex items-start justify-between gap-4 py-3"
							key={grantKey(grant)}
						>
							<div className="min-w-0 space-y-1 text-sm">
								<div>
									<span className="font-medium">{holder.label}</span>
									{holder.detail ? (
										<>
											{" "}
											<span className="text-muted-foreground">
												({holder.detail})
											</span>
										</>
									) : null}
								</div>
								<div>
									{grant.organizationName}
									{grant.organizationEnabled ? null : (
										<span className="text-muted-foreground">
											{" "}
											(Switched off)
										</span>
									)}{" "}
									· {grant.workspaceName}
								</div>
								<div className="text-muted-foreground">
									Since {formatSince(grant.since)}
								</div>
								<div>
									{grant.reason ?? "No reason recorded for this grant."}
								</div>
							</div>
							<Button
								aria-label={`End ${holder.label}'s access to ${grant.workspaceName}`}
								onClick={() => onEnd(grant)}
								size="sm"
								variant="outline"
							>
								End access
							</Button>
						</li>
					);
				})}
				{shown.length === 0 ? (
					<li className="py-3 text-muted-foreground text-sm">
						Nobody matches “{search.trim()}”.
					</li>
				) : null}
			</ul>
		</div>
	);
}
